/**
 * Assignee display helpers for the incident / observation / hazard lists.
 *
 * QA 2026-08-19: the Incidents List "Assigned To" column used to render
 * `assignee || reported_by_name`, so an UNASSIGNED incident displayed the
 * REPORTER's name — "Admin" for an admin-filed report — while the detail and
 * edit views read only `assignee` and correctly showed "Not assigned". The two
 * views disagreed because they were rendering different fields under the same
 * heading.
 *
 * The reporter is never the assignee. Assignment has exactly two states, and
 * this helper is the single place that maps them to display text so the desktop
 * table and the mobile card cannot drift apart again.
 */

/** Text shown when nothing is assigned. Kept in one place for consistency. */
export const UNASSIGNED_LABEL = 'Unassigned';

/**
 * Display text for a record's "Assigned To" field.
 *
 * Deliberately reads ONLY `assignee`. Do not add a fallback to
 * `reported_by_name`, `reported_by`, or `created_by` — that is the exact defect
 * this replaced, and it makes the list contradict the detail view.
 *
 * @param {object} record incident / observation / hazard row from the API
 * @returns {string} the assignee name, or UNASSIGNED_LABEL
 */
export function formatAssignee(record) {
  const name = record?.assignee;
  if (typeof name !== 'string') return UNASSIGNED_LABEL;
  const trimmed = name.trim();
  return trimmed === '' ? UNASSIGNED_LABEL : trimmed;
}
